import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { FaShare, FaTrash, FaDownload, FaSpinner } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, handleLogout } = useAuth();
  const [tryOns, setTryOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchTryOns();
  }, [navigate, isAuthenticated]);

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const handleShare = async (tryOn) => {
    try {
      const shareData = {
        title: 'Check out my new hairstyle from AI Hairstyle Studio!',
        text: 'Transform your look with AI-powered hairstyle transformations.',
        url: window.location.origin // Your website URL
      };

      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        // Fallback for browsers that don't support native sharing
        await navigator.clipboard.writeText(
          `${shareData.title}\n${shareData.text}\n${shareData.url}`
        );
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share');
    }
  };

  const handleDeleteTryOn = async (id) => {
    if (!window.confirm('Are you sure you want to delete this try-on?')) {
      return;
    }

    setDeleteLoading(id);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/user/tryons/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete try-on');
      }

      setTryOns(current => current.filter(tryon => tryon.id !== id));
      toast.success('Try-on deleted successfully');
    } catch (error) {
      console.error('Error deleting try-on:', error);
      toast.error('Failed to delete try-on');
    } finally {
      setDeleteLoading(null);
    }
  };

  const fetchTryOns = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      if (!headers.Authorization) {
        console.error('No auth token found');
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/user/tryons`, {
        headers: {
          ...headers,
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch try-ons');
      }

      const data = await response.json();
      console.log('Fetched try-ons:', data);
      
      if (data && data.success && Array.isArray(data.tryons)) {
        setTryOns(data.tryons);
      } else {
        setTryOns([]);
        console.error('Invalid try-ons data format:', data);
      }
    } catch (error) {
      console.error('Error fetching try-ons:', error);
      setTryOns([]);
      toast.error('Failed to load your try-ons');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#E5F5F5] py-12 px-4 sm:px-6 lg:px-8">
      <ToastContainer />
      
      {/* Header Section */}
      <div className="max-w-7xl mx-auto mb-12">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Hairstyle Collection</h1>
            <p className="text-gray-600">Your personalized AI-generated hairstyle transformations</p>
          </div>
          <Link
            to="/studio-hair-salon"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#4EC5C1] hover:bg-[#3BA7A3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4EC5C1] shadow-lg transform transition hover:-translate-y-0.5"
          >
            Try New Hairstyle
          </Link>
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <FaSpinner className="animate-spin text-4xl text-[#4EC5C1]" />
        </div>
      ) : error ? (
        <div className="text-center text-red-500 bg-red-50 p-4 rounded-lg shadow">
          {error}
        </div>
      ) : tryOns.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg shadow-lg">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              No Hairstyles Yet
            </h2>
            <p className="text-gray-600 mb-6">
              Start your transformation journey by trying on a new hairstyle!
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#4EC5C1] hover:bg-[#3BA7A3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4EC5C1] shadow-lg"
          >
            Create Your First Try-On
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {tryOns.map((tryOn) => (
            <motion.div
              key={tryOn.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl overflow-hidden transform transition duration-300 hover:scale-[1.02] hover:shadow-2xl"
            >
              <div className="relative aspect-w-4 aspect-h-3">
                {tryOn.generated_image ? (
                  <img
                    src={tryOn.generated_image}
                    alt="Generated Hairstyle"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/placeholder.png';
                      console.error('Error loading image:', tryOn.generated_image);
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <span className="text-gray-400">Image not available</span>
                  </div>
                )}
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
                  <div className="flex gap-4">
                    <button
                      onClick={() => handleShare(tryOn)}
                      className="p-2 bg-white rounded-full hover:bg-[#E5F5F5] transition-colors duration-200"
                      title="Share"
                    >
                      <FaShare className="w-5 h-5 text-[#4EC5C1]" />
                    </button>
                    <a
                      href={tryOn.generated_image}
                      download
                      className="p-2 bg-white rounded-full hover:bg-[#E5F5F5] transition-colors duration-200"
                      title="Download"
                    >
                      <FaDownload className="w-5 h-5 text-[#4EC5C1]" />
                    </a>
                    <button
                      onClick={() => handleDeleteTryOn(tryOn.id)}
                      disabled={deleteLoading === tryOn.id}
                      className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors duration-200"
                      title="Delete"
                    >
                      {deleteLoading === tryOn.id ? (
                        <FaSpinner className="w-5 h-5 text-red-600 animate-spin" />
                      ) : (
                        <FaTrash className="w-5 h-5 text-red-600" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 font-medium">
                    {formatDate(tryOn.created_at)}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#4EC5C1] bg-[#E5F5F5] px-2 py-1 rounded-full">
                      AI Generated
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
